import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useStaticQuery, graphql, Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import Layout from "../components/layout/Layout";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import Components from "../components/content/Components"

import Feeds from "../components/articles/Feeds";
import FeedsList from "../components/articles/FeedsList";
import CalendarFeed from "../components/articles/CalendarFeed";
import CalendarFeedList from "../components/articles/CalendarFeedList";

const TagPage = ({ data }) => {
  const tag = data.tag;
  const articles = data.strapiArticle;
  const tagSlug = tag.slug;
  const components = tag.components || [];
  let feedsProps = {};
  let feedsListProps = {};

  // Check tag slug prefixes to set the appropriate props for Feeds and FeedsList components
  switch (true) {
    case tagSlug.startsWith("people"):
      feedsProps = { articles: data.peopletiles.nodes };
      feedsListProps = { articles: data.peoplelist.nodes };
      break;
    case tagSlug.startsWith("society"):
      feedsProps = { articles: data.societytiles.nodes };
      feedsListProps = { articles: data.societylist.nodes };
      break;
    case tagSlug.startsWith("planet"):
      feedsProps = { articles: data.planettiles.nodes };
      feedsListProps = { articles: data.planetlist.nodes };
      break;
    case tagSlug.startsWith("hubs"):
      feedsProps = { articles: data.hubs.nodes };
      feedsListProps = { articles: data.hubslist.nodes };
      break;
    case tagSlug.startsWith("study"):
      feedsProps = { articles: data.study.nodes };
      feedsListProps = { articles: data.studylist.nodes };
      break;
    case tagSlug.startsWith("fellow"):
      feedsProps = { articles: data.fellows.nodes };
      feedsListProps = { articles: data.fellowlist.nodes };
      break;
    case tagSlug.startsWith("environment"):
      feedsProps = { articles: data.env.nodes };
      feedsListProps = { articles: data.envlist.nodes };
      break;
    case tagSlug.startsWith("apply"):
      feedsProps = { articles: data.apply.nodes };
      feedsListProps = { articles: data.applylist.nodes };
      break;
    case tagSlug.startsWith("news"):
      feedsProps = { articles: data.news.nodes };
      feedsListProps = { articles: data.newslist.nodes };
      break;
    case tagSlug.startsWith("calendar"):
      feedsProps = { articles: data.calendar.nodes };
      feedsListProps = { articles: data.calendarlist.nodes };
      break;
    case tagSlug.startsWith("risktaker"):
      feedsProps = { articles: data.risktaker.nodes };
      break;
    case tagSlug.startsWith("europe"):
      feedsProps = { articles: data.europe.nodes };
      break;
    case tagSlug.startsWith("arts"):
      feedsProps = { articles: data.arts.nodes };
      break;
    default:
      break;
  }

  const isGerman = typeof window !== 'undefined' && window.location.pathname.includes('/de');
  const hasIntl = data.tag.localizations

  const [isCalendarPage, setIsCalendarPage] = useState(false);

  useEffect(() => {
    setIsCalendarPage(tagSlug.startsWith("calendar"));
  }, [tagSlug]);

  return (
    <Layout as="article">
      <Header data={hasIntl} />
        <Components components={components || []} />
        {!isCalendarPage && feedsProps.articles && feedsProps.articles.length > 0 && <Feeds {...feedsProps} />}
        {!isCalendarPage && feedsListProps.articles && feedsListProps.articles.length > 0 && <FeedsList {...feedsListProps} />}
        {tagSlug.startsWith("calendar") && feedsProps.articles && feedsProps.articles.length > 0 && <CalendarFeed articles={feedsProps.articles} />}
        {tagSlug.startsWith("calendar") && feedsListProps.articles && feedsListProps.articles.length > 0 && <CalendarFeedList articles={feedsListProps.articles} />}
      <Footer />
    </Layout>
  );
};

export const pageQuery = graphql`
  query ($slug: String) {
    strapiGlobal(locale: {eq: "de"}) {
      siteName
      siteDescription
      favicon {
        file {
          url
        }
      }
      defaultSeo {
        metaTitle
        metaDescription
        shareImage {
          file {
            url
          }
        }
      }
    }
    tag: strapiTag(slug: {eq: $slug}, locale: {eq: "de"}) {
      name
      description
      slug
      localizations {
        slug
        localizations {
          slug
        }
      }
      components {
        __typename
        ... on StrapiComponentChartsPieChart {
          id
          PiechartText
          PiechartDesc
          PiechartValue {
            PiechartValuepart
            PiechartValuepartdesc
          }
          PiechartSpacing
          PiechartLegend
          ColorTheme
        }
        ... on StrapiComponentChartsPercentageBarChart {
          id
          PercentageBarcharttext
          PercentageBarchartdescleft
          PercentageBarchartdescright
          PercentageBarchartentry {
            PercentagebarchartColumvalue
            PercentagebarchartColumvaluetwo
            PercentagebarchartColumdesc
            PercentagebarColumvaluethree
          }
          PercentageBarchartgroup
          PercentageBarchartspacing
          PercentagebarchartLegend
          PercentagebarLegendentry {
            PercentagebarLegendentryname
          }
          PercentageBarchartColorTheme
        }
        ... on StrapiComponentChartsBarChart {
          id
          BarchartText
          BarchartDescleft
          BarchartDescright
          BarchartEntry {
            BarchartColumnvalue
            BarchartColumndesc
          }
          BarchartColumngroup
          BarchartSpacing
          BarchartLegend
          BarchartLegendentry {
            BarchartLegendentryname
          }
          BarchartColorTheme
        }
        ... on StrapiComponentChartsCompareChart {
          id
          ComparechartText
          ComparechartTextone
          ComparechartTexttwo
          ComparechartLegendentry {
            ComparechartLegendentryname
          }
          ComparechartLegend
          ComparechartValue {
            ComparechartValueone
            ComparechartValuetwo
            ComparechartValuedesc
          }
          ComparechartSpacing
          ComparechartColorTheme
        }
        ... on StrapiComponentContainerAccordeonHighlight {
          id
          Headline
          Margins
          Subheadline
          Text
          strapiId
        }
        ... on StrapiComponentContentStickyQuote {
          id
          stickyQuote
          stickyQuoteauthor
          stickyQuoteDirection
          stickyQuotemedia {
            caption
            name
            alternativeText
            file {
              childImageSharp {
                gatsbyImageData(
                  aspectRatio: 1.7
                  layout: FULL_WIDTH
                  placeholder: DOMINANT_COLOR
                )
              }
            }
          }
          strapiId
        }
        ... on StrapiComponentContainerDataAnimation {
          id
          DataAnimationCols
          DataAnimationLayout
          Text
          Animations {
            DataAniumber
            NumberDescription
            NumberUnit
            strapiId
            id
          }
          strapiId
        }
        ... on StrapiComponentContainerFaq {
          id
          strapiId
          Text
          Headline
          FAQ {
            Answer
            Question
            id
            strapiId
          }
        }
        ... on StrapiComponentContentSpecialsStickyTextAnimation {
          id
          strapiId
          TextFold
          Text
          Headline
          Animations {
            DecimalNumber
            NumberDescription
            NumberUnit
            id
            strapiId
          }
        }
        ... on StrapiComponentContentSpecialsStickyTextInlineMedia {
          Text
          TextFold
          strapiId
          Headline
          captionText
          portraitMode
          Media {
            alternativeText
            caption
            file {
              childImageSharp {
                gatsbyImageData(quality:75, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
            mime
            name
            url
          }
        }
        ... on StrapiComponentContentSpecialsStickyTextInlineQuote {
          id
          Author
          AuthorDesc
          Headline
          Quote
          Text
          TextFold
          strapiId
        }
        ... on StrapiComponentContentQuotes {
          id
          Author
          Quote
          strapiId
        }
        ... on StrapiComponentContentReadMore {
          id
          Headline
          Text
          strapiId
        }
        ... on StrapiComponentContentRichTexts {
          id
          RichMargins
          Text
          strapiId
        }
        ... on StrapiComponentContentStickyMedia {
          Direction
          Headline
          Text
          strapiId
          portraitMode
          captionText
          Media {
            alternativeText
            caption
            mime
            name
            url
            file {
              childImageSharp {
                gatsbyImageData(quality:75, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
        }
        ... on StrapiComponentContentStickyText {
          id
          Headline
          Text
          strapiId
        }
        ... on StrapiComponentImagesCarousels {
          LayoutCarousel
          strapiId
          CarouselEntry {
            strapiId
            captionText
            Media {
              alternativeText
              caption
              name
              mime
              strapiId
              url
              file {
                childImageSharp {
                  gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
                }
                name
                url
              }
            }
          }
        }
        ... on StrapiComponentImagesGalery {
          strapiId
          portraitMode
          GaleryEntries {
            Headline
            Subheadline
            Text
            id
            strapiId
            Media {
              caption
              alternativeText
              mime
              name
              url
              file {
                childImageSharp {
                  gatsbyImageData(quality:75, placeholder: DOMINANT_COLOR)
                }
                name
                url
              }
            }
          }
        }
        ... on StrapiComponentImagesHorizontalGalery {
          id
          strapiId
          HorizontalEntries {
            Text
            Title
            id
            strapiId
            Media {
              alternativeText
              caption
              url
              mime
              name
              file {
                childImageSharp {
                  gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
                }
                url
                name
              }
            }
          }
        }
        ... on StrapiComponentImagesImage {
          ImageMargins
          ImageSize
          LinkTarget
          LinkURL
          strapiId
          captionText
          portraitMode
          Media {
            alternativeText
            caption
            mime
            file {
              childImageSharp {
                gatsbyImageData(quality:75, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
        }
        ... on StrapiComponentImagesParallaxTeaser {
          id
          AuthorDescriptionParallaxTeaser
          AuthorParallaxTeaser
          LayoutParallaxTeaser
          Text
          ParallaxMedia {
            caption
            alternativeText
            mime
            name
            url
            file {
              childImageSharp {
                gatsbyImageData(quality:75, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
        }
        ... on StrapiComponentImagesStages {
          Headlinecolor
          Layout
          Subheadline
          Text
          strapiId
          captionText
          Media {
            alternativeText
            caption
            mime
            name
            url
            file {
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
        }
        ... on StrapiComponentLinksBookmarks {
          id
          strapiId
          articles {
            cover {
              alternativeText
              caption
              mime
              url
              name
              file {
                childImageSharp {
                  gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
                }
                name
                url
              }
            }
            tags {
              name
              slug
            }
            title
            slug
            strapiId
            description
            layout
            locale
            localizations {
              localizations {
                slug
              }
              slug
            }
          }
        }
        ... on StrapiComponentLinksContentTeaser {
          id
          Headline
          ModalboxContent
          Subheadline
          TeaserDirection
          TeaserMargins
          Text
          Media {
            alternativeText
            caption
            mime
            name
            url
            file {
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
          ModalboxImage {
            alternativeText
            caption
            mime
            name
            url
            file {
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
              name
              url
            }
          }
          strapiId
        }
        ... on StrapiComponentLinksDownloads {
          id
          Text
          strapiId
          Downloads {
            Name
            id
            strapiId
            Download {
              alternativeText
              caption
              mime
              name
              url
              file {
                url
              }
            }
          }
        }
        ... on StrapiComponentLinksHighlight {
          id
          strapiId
          Subheadline
          LinkURL
          LinkTargets
          Headline
          ComponentMargins
          Download {
            caption
            alternativeText
            file {
              url
            }
            mime
          }
          HighlightPreviews: HighlightPreview {
            alternativeText
            caption
            file {
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
            }
          }
        }
        ... on StrapiComponentLinksLinkList {
          id
          strapiId
          Links {
            LinkTargets
            LinkURL
            Title
            id
            strapiId
          }
        }
        ... on StrapiComponentVideosInternalVideo {
          id
          videoCaption
          Cover {
            alternativeText
            caption
            mime
            name
            url
            file {
              name
              url
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
            }
          }
          Video {
            alternativeText
            caption
            mime
            name
            url
            file {
              name
              url
            }
          }
          Subtitle {
            alternativeText
            caption
            mime
            name
            url
            file {
              url
              name
              childImageSharp {
                gatsbyImageData(quality:75, aspectRatio: 1.7, layout: FULL_WIDTH, placeholder: DOMINANT_COLOR)
              }
            }
          }
        }
        ... on StrapiComponentVideosYoutubeVideo {
          id
          VideoCaption
          YoutubeIds
          strapiId
        }
      }
    }
    allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    peopletiles: allStrapiArticle(
      filter: {options: {eq: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    peoplelist: allStrapiArticle(
      filter: {options: {ne: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    societytiles: allStrapiArticle(
      filter: {options: {eq: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    societylist: allStrapiArticle(
      filter: {options: {ne: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    planettiles: allStrapiArticle(
      filter: {options: {eq: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    planetlist: allStrapiArticle(
      filter: {options: {ne: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    hubs: allStrapiArticle(
      filter: {options: {eq: "Hub"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    hubslist: allStrapiArticle(
      filter: {options: {ne: "Hub"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    fellows: allStrapiArticle(
      filter: {options: {eq: "Fellow"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    fellowlist: allStrapiArticle(
      filter: {options: {ne: "Fellow"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    study: allStrapiArticle(
      filter: {options: {eq: "Study"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    studylist: allStrapiArticle(
      filter: {options: {ne: "Study"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    env: allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    envlist: allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    apply: allStrapiArticle(
      filter: {options: {eq: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    applylist: allStrapiArticle(
      filter: {options: {ne: "Apply"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    news: allStrapiArticle(
      filter: {options: {eq: "News"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    newslist: allStrapiArticle(
      filter: {options: {ne: "News"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ListCard
      }
    }
    calendar: allStrapiArticle(
      filter: {options: {eq: "Event"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {eventdate: ASC}
    ) {
      nodes {
        ...CalendarCard
      }
    }
    calendarlist: allStrapiArticle(
      filter: {options: {ne: "Event"}, tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {eventdate: ASC}
    ) {
      nodes {
        ...CalendarCardList
      }
    }
    risktaker: allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    europe: allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
    arts: allStrapiArticle(
      filter: {tags: {elemMatch: {slug: {eq: $slug}}}, locale: {eq: "de"}}
      sort: {publishedAt: DESC}
    ) {
      nodes {
        ...ArticleCard
      }
    }
  }
`

export default TagPage

export function Head({ data }) {

  const tag = data.tag
    const global = data.strapiGlobal

  const seo = {
    metaDescription: tag.description,
    siteName: tag.name,
    slug: tag.slug,
    imageUrl: global.defaultSeo.shareImage.file.url,
  }

  return (
    <>
      <body className={`${tag.name}`} />
      <title>{`${seo.siteName} — Allianz Foundation`}</title>
      <meta name="description" content={`${seo.metaDescription}`} />
      <meta name="image" content={`${seo.imageUrl}`} />
      <meta property="og:title" content={`${seo.siteName}`} />
      <meta property="og:description" content={`${seo.metaDescription}`} />
      <meta property="og:image" content={`${seo.imageUrl}`} />
      <meta name="twitter:title" content={`${seo.siteName}`} />
      <meta name="twitter:description" content={`${seo.metaDescription}`} />
      <meta name="twitter:image" content={`${seo.imageUrl}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="robots" content="index,follow" />
      <meta name="googlebot" content="index,follow" />
      <link rel="canonical" href={`https://allianzfoundation.org/de/${seo.slug}/`} />
    </>
  )
}
